import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiStatus, NotificationPosition } from 'core/enums';
import { useFocusFirstInvalidField } from 'core/hooks';
import { useFormContext, useNotificationContext } from 'core/states';
import { getFirstErrorMessageOnScreen } from 'core/utils';

import { AttributeFormFields } from './AttributeFormFields';
import { AttributePriorityMode } from './mappers';
import { IAttributeForm } from '../../types';

export interface IAttributesHandle {
  triggerFormSubmit: () => Promise<IAttributeForm | null>;
  getFormValues: () => IAttributeForm;
}

interface IProps {
  hasSubmitted: boolean;
  hydratedPriorityMode?: AttributePriorityMode;
  // Caps the Trade Agreement Start Date picker; absent leaves it unbounded.
  validUntil?: string | null;
  isReadOnly?: boolean;
}

export const Attributes = forwardRef<IAttributesHandle, IProps>(function Attributes(
  { hasSubmitted, hydratedPriorityMode = null, validUntil = null, isReadOnly = false },
  ref
) {
  const { t } = useTranslation();
  const { clearErrors, errors, focusRequest, getValues, handleSubmit } = useFormContext();
  const { showNotification } = useNotificationContext();

  useImperativeHandle(ref, () => ({
    // Raw, unvalidated snapshot — capturing in-progress input must not block on validation.
    getFormValues: () => getValues() as IAttributeForm,
    triggerFormSubmit: () =>
      new Promise<IAttributeForm | null>((resolve) => {
        handleSubmit(
          (data: IAttributeForm) => resolve(data),
          (validationErrors) => {
            showNotification({
              type: ApiStatus.ERROR,
              title: t('titleValidationError'),
              message: getFirstErrorMessageOnScreen(validationErrors) ?? t('serverError'),
              position: NotificationPosition.DRAWER,
            });
            resolve(null);
          }
        )();
      }),
  }));

  useEffect(() => {
    if (!hasSubmitted && (errors.project || errors.customers)) {
      clearErrors(['project', 'customers']);
    }
  }, [hasSubmitted, errors.project, errors.customers, clearErrors]);

  // handleSubmit focuses in registration order and skips the chip lists (they register no ref), so
  // this moves the focus to the invalid field shown first, chip lists included.
  useFocusFirstInvalidField(errors, focusRequest);

  return (
    <AttributeFormFields
      hydratedPriorityMode={hydratedPriorityMode}
      validUntil={validUntil}
      isReadOnly={isReadOnly}
    />
  );
});
